import { safeFetch, logError } from '../utils/errorHandler';

// In-memory cache for loaded data
const cache = new Map();

const BASE_PATH = '/data';

/**
 * Fetches JSON data from a given path with caching
 */
async function fetchJSON(path) {
  if (cache.has(path)) {
    return cache.get(path);
  }

  try {
    const response = await safeFetch(path);
    const data = await response.json();
    cache.set(path, data);
    return data;
  } catch (error) {
    logError('contentService.fetchJSON', error, { path });
    throw error;
  }
}

/**
 * Gets all post metadata
 * @returns {Promise<Array>} Array of post metadata objects
 */
export async function getPostsMetadata() {
  return fetchJSON(`${BASE_PATH}/posts.json`);
}

/**
 * Gets full content for a specific post by slug
 * @param {string} slug - The post slug
 * @returns {Promise<Object>} Post object with full content
 */
export async function getPostContent(slug) {
  return fetchJSON(`${BASE_PATH}/posts/${slug}.json`);
}

/**
 * Gets the directory tree structure
 * @returns {Promise<Object>} Tree structure object
 */
export async function getDirectoryTree() {
  return fetchJSON(`${BASE_PATH}/tree.json`);
}

/**
 * Gets tags and categories mapping
 * @returns {Promise<Object>} Object with tags and categories
 */
export async function getTags() {
  return fetchJSON(`${BASE_PATH}/tags.json`);
}

/**
 * Gets the search index
 * @returns {Promise<Object>} Search index data
 */
export async function getSearchIndex() {
  return fetchJSON(`${BASE_PATH}/search-index.json`);
}

/**
 * Gets posts by tag
 * @param {string} tag - The tag name
 * @returns {Promise<Array>} Array of post metadata for the tag
 */
export async function getPostsByTag(tag) {
  const [tagsData, postsMetadata] = await Promise.all([
    getTags(),
    getPostsMetadata()
  ]);

  const postSlugs = tagsData.tags[tag.toLowerCase()] || [];
  return postsMetadata.filter(post => postSlugs.includes(post.slug));
}

/**
 * Gets posts by category
 * @param {string} category - The category name
 * @returns {Promise<Array>} Array of post metadata for the category
 */
export async function getPostsByCategory(category) {
  const [tagsData, postsMetadata] = await Promise.all([
    getTags(),
    getPostsMetadata()
  ]);

  const postSlugs = tagsData.categories[category.toLowerCase()] || [];
  return postsMetadata.filter(post => postSlugs.includes(post.slug));
}

/**
 * Clears the cache (useful for development)
 */
export function clearCache() {
  cache.clear();
}
